import { SsccConfiguration, UpdateSsccConfigurationPayload } from 'services/repositories/sscc/sscc';

export const GS1_PREFIX_PATTERN = /^\d{6,12}$/;

/** The printer resolutions the API accepts: 6 | 8 | 12 | 24. */
export type Dpmm = NonNullable<NonNullable<SsccConfiguration['label']>['dpmm']>;

const DPMM_VALUES: Array<Dpmm> = [6, 8, 12, 24];

export const DPMM_OPTIONS = DPMM_VALUES.map((dpmm) => ({
  value: dpmm,
  label: `${dpmm} dpmm`,
  testId: `dpmm-${dpmm}-option`,
}));

/**
 * The sections the current partner owns, read from the ones the GET response carries: a supplier gets
 * `generation` and `label`, a 3PL only `label`, and a buyer `requirement` on top of any of those. The PATCH
 * rejects a section the partner does not own, so the page edits and sends exactly these.
 */
export type SsccOwnedSections = Record<'generation' | 'label' | 'requirement', boolean>;

export const getSsccOwnedSections = (configuration: SsccConfiguration): SsccOwnedSections => ({
  generation: configuration.generation !== undefined,
  label: configuration.label !== undefined,
  requirement: configuration.requirement !== undefined,
});

/**
 * The field names mirror the PATCH payload paths rather than the GET response, so 422 keys such as
 * `generation.gs1_prefix` resolve straight onto the inputs through `processSubmissionErrors`. Note the
 * asymmetry: the response calls the prefix `gs1_company_prefix`, the request calls it `gs1_prefix`.
 */
export type SsccSettingsFormState = {
  generation: {
    gs1_prefix: string;
    counter_seed: string;
  };
  label: {
    dpmm: Dpmm | undefined;
  };
  requirement: {
    shipping_unit_of_measure: string | null;
  };
};

export const DEFAULT_SSCC_SETTINGS_FORM_VALUES: SsccSettingsFormState = {
  generation: {
    gs1_prefix: '',
    counter_seed: '',
  },
  label: {
    dpmm: undefined,
  },
  requirement: {
    shipping_unit_of_measure: null,
  },
};

/**
 * The live counter is deliberately absent: it is server state the serial-number field only displays, and
 * keeping it out of the form guarantees it can never be submitted back as a seed.
 */
export const getSsccSettingsFormValues = (configuration: SsccConfiguration | undefined): SsccSettingsFormState => ({
  generation: {
    gs1_prefix:
      configuration?.generation?.gs1_company_prefix ?? DEFAULT_SSCC_SETTINGS_FORM_VALUES.generation.gs1_prefix,
    counter_seed: DEFAULT_SSCC_SETTINGS_FORM_VALUES.generation.counter_seed,
  },
  label: {
    dpmm: configuration?.label?.dpmm ?? DEFAULT_SSCC_SETTINGS_FORM_VALUES.label.dpmm,
  },
  requirement: {
    shipping_unit_of_measure:
      configuration?.requirement?.shipping_unit_of_measure ??
      DEFAULT_SSCC_SETTINGS_FORM_VALUES.requirement.shipping_unit_of_measure,
  },
});

/**
 * The seed is optional — empty means "start from the beginning" — but when given it must be a whole number
 * greater than zero, because serial numbering starts at 1. `0` would otherwise pass a digits-only check and
 * be sent as a seed the supplier cannot actually issue.
 */
export const isValidFirstSerialNumber = (value: string | undefined) =>
  value === undefined || value === '' || (/^\d+$/.test(value) && Number(value) > 0);

/**
 * The backend only applies a seed when the prefix actually changes — a seed sent for an already-seeded
 * prefix is ignored so the live counter is never rewound. Outside that window the serial-number field
 * reports the counter read-only instead of promising an edit the API would discard.
 */
export const canSeedCounter = (storedPrefix: string | null, enteredPrefix: string) =>
  storedPrefix === null || enteredPrefix !== storedPrefix;

/**
 * Only owned sections are sent. The requirement always goes out, because `null` is how a buyer clears its
 * unit. The seed goes out only inside the window where the backend applies it, so the displayed counter is
 * never sent back as a seed.
 */
export const buildSsccUpdatePayload = (
  submission: SsccSettingsFormState,
  { owned, seedable }: { owned: SsccOwnedSections; seedable: boolean },
): UpdateSsccConfigurationPayload => {
  const { gs1_prefix, counter_seed } = submission.generation;
  const { dpmm } = submission.label;

  return {
    ...(owned.generation && {
      generation: {
        gs1_prefix,
        ...(seedable && counter_seed !== '' ? { counter_seed: Number(counter_seed) } : {}),
      },
    }),
    ...(owned.label && dpmm !== undefined && { label: { dpmm } }),
    ...(owned.requirement && { requirement: submission.requirement }),
  };
};
